/**
 * Area and group are functions of the plan already in memory.
 * The Todos area returns every plan passed in.
 */
import { CATEGORIES } from "../data/catalog";
import { Plan } from "../types";
import { normalize } from "./format";

export type PlanArea = "todos" | "seguranca-publica" | "vestibulares";

export const PLAN_AREAS: readonly { id: PlanArea; label: string }[] = [
  { id: "todos", label: "Todos" },
  { id: "seguranca-publica", label: "Segurança Pública" },
  { id: "vestibulares", label: "Vestibulares" },
];

export type PlanGroupId = "cbmgo" | "choa-tap" | "pmgo" | "outros";

export const PLAN_GROUPS: readonly { id: PlanGroupId; label: string }[] = [
  { id: "cbmgo", label: "CBMGO" },
  { id: "choa-tap", label: "CHOA e TAP" },
  { id: "pmgo", label: "PMGO" },
  { id: "outros", label: "Outros concursos" },
];

export interface PlanGroup {
  id: PlanGroupId;
  label: string;
  plans: Plan[];
}

const VESTIBULAR_WORDS = ["vestibular", "medicina", "ueg"] as const;

function isAlphanumeric(char: string): boolean {
  return (char >= "a" && char <= "z") || (char >= "0" && char <= "9");
}

/** Word match on accent-stripped text: bounded by start, end, or a non-alphanumeric character. */
function hasWord(text: string, word: string): boolean {
  const haystack = normalize(text);
  const needle = normalize(word);
  if (!needle) return false;

  let from = 0;
  while (from <= haystack.length - needle.length) {
    const index = haystack.indexOf(needle, from);
    if (index === -1) return false;
    const before = index === 0 ? "" : haystack.charAt(index - 1);
    const afterIndex = index + needle.length;
    const after = afterIndex >= haystack.length ? "" : haystack.charAt(afterIndex);
    if ((!before || !isAlphanumeric(before)) && (!after || !isAlphanumeric(after))) return true;
    from = index + 1;
  }
  return false;
}

function hasAnyWord(text: string, words: readonly string[]): boolean {
  return words.some((word) => hasWord(text, word));
}

export function catalogEntry(plan: Plan): string {
  const categoryLabel = CATEGORIES.find((category) => category.id === plan.categoryId)?.label ?? plan.categoryId;
  return [
    plan.description,
    plan.longDescription ?? "",
    plan.slug,
    plan.benefits.join(" "),
    plan.categoryId,
    categoryLabel,
  ].join(" ");
}

export function isVestibularPlan(plan: Plan): boolean {
  const entry = catalogEntry(plan);
  return hasAnyWord(plan.name, VESTIBULAR_WORDS) || hasAnyWord(entry, VESTIBULAR_WORDS);
}

export function isSegurancaPublicaPlan(plan: Plan): boolean {
  return plan.categoryId === "seguranca-publica" || plan.categoryId === "choa-tap";
}

export function matchesArea(plan: Plan, area: PlanArea): boolean {
  switch (area) {
    case "todos":
      return true;
    case "seguranca-publica":
      return isSegurancaPublicaPlan(plan);
    case "vestibulares":
      return isVestibularPlan(plan);
    // no default
  }
}

export function matchesPlanSearch(plan: Plan, query: string): boolean {
  const q = normalize(query);
  if (!q) return true;
  return normalize(`${plan.name} ${catalogEntry(plan)}`).includes(q);
}

export function visiblePlans(plans: Plan[], area: PlanArea, query: string): Plan[] {
  return plans.filter((plan) => matchesArea(plan, area) && matchesPlanSearch(plan, query));
}

export function planGroupId(plan: Plan): PlanGroupId {
  if (hasWord(plan.name, "cbmgo")) return "cbmgo";
  if (hasWord(plan.name, "choa") || hasWord(plan.name, "tap")) return "choa-tap";
  if (hasWord(plan.name, "pmgo")) return "pmgo";
  return "outros";
}

export function groupVisiblePlans(plans: Plan[]): PlanGroup[] {
  const byId = new Map<PlanGroupId, Plan[]>();
  for (const plan of plans) {
    const id = planGroupId(plan);
    const bucket = byId.get(id);
    if (bucket) bucket.push(plan);
    else byId.set(id, [plan]);
  }

  const groups: PlanGroup[] = [];
  for (const group of PLAN_GROUPS) {
    const groupPlans = byId.get(group.id);
    if (groupPlans && groupPlans.length > 0) {
      groups.push({ id: group.id, label: group.label, plans: groupPlans });
    }
  }
  return groups;
}

export function isChoaOrTapPlan(plan: Plan): boolean {
  return plan.categoryId === "choa-tap" || hasAnyWord(plan.name, ["choa", "tap"]);
}

export function isPmgoSoldadoPlan(plan: Plan): boolean {
  return hasWord(plan.name, "pmgo") && hasWord(plan.name, "soldado");
}

export function isGcmPlan(plan: Plan): boolean {
  return hasWord(plan.name, "gcm");
}
